import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fmtMoney } from "@/lib/api";
import { ArrowRight } from "lucide-react";

function ProductCard({ p, store }) {
  const acc = store.secondary_color || "#3DBB2C";
  const promo = p.sale_price && p.sale_price < p.price;
  return (
    <Link to={`/store/${store.slug}/produit/${p.id}`} className="group" data-testid="storefront-product-card">
      <div className="aspect-[3/4] bg-neutral-100 rounded-lg overflow-hidden relative">
        {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        {promo && <span className="absolute top-3 left-3 text-xs font-semibold text-white px-2 py-1 rounded" style={{ background: acc }}>Promo</span>}
      </div>
      <div className="mt-3">
        <div className="text-sm text-neutral-900 font-medium">{p.name}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className="mono text-sm" style={{ color: acc }}>{fmtMoney(promo ? p.sale_price : p.price)}</span>
          {promo && <span className="mono text-xs text-neutral-400 line-through">{fmtMoney(p.price)}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function Home({ data }) {
  const store = data.store;
  const acc = store.secondary_color || "#3DBB2C";
  const banner = store.banner || "https://images.unsplash.com/photo-1445205170230-053b83016050?w=1600&q=80";
  return (
    <div>
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={banner} alt={store.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.55))" }} />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-5 w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="overline-label text-white/80">{store.city} {store.country}</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-3 max-w-xl leading-[1.05]">{store.name}</h1>
              <p className="text-white/80 mt-4 max-w-md">Découvrez notre collection soigneusement sélectionnée.</p>
              <Link to={`/store/${store.slug}/catalogue`}>
                <button className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium hover:opacity-90 transition-opacity" style={{ background: acc }} data-testid="storefront-hero-cta">
                  Découvrir le catalogue <ArrowRight size={18} />
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {data.categories?.length > 0 && (
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap gap-3">
          {data.categories.map((c) => (
            <Link key={c} to={`/store/${store.slug}/catalogue?category=${encodeURIComponent(c)}`}
              className="px-4 py-2 rounded-full border border-black/10 text-sm text-neutral-700 hover:border-black/40 transition-colors bg-white">{c}</Link>
          ))}
        </div>
      )}

      {data.featured?.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 py-8">
          <h2 className="font-display text-3xl font-bold text-neutral-900 mb-6">Produits vedettes</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">{data.featured.map((p) => <ProductCard key={p.id} p={p} store={store} />)}</div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-5 py-8">
        <h2 className="font-display text-3xl font-bold text-neutral-900 mb-6">Nouveautés</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">{data.new.map((p) => <ProductCard key={p.id} p={p} store={store} />)}</div>
      </section>
    </div>
  );
}
